'use client';

import Dialog from '@/components/Dialog';
import Image from '@/components/Image';
import Popup from '@/components/Popup';
import { EPlatform } from '@/utils/enums/platform-enum';
import { CloseOutlined } from '@zeelool/icon';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type {
  StyleHotProductStyleQuizData,
  StyleHotProductStyleQuizQuestion,
  StyleHotProductStyleQuizResult,
} from '../types';
import { calculateMatchedStyleTitle } from '../utils';
import {
  getStyleQuizPopupContent,
  getStyleQuizExposurePageKey,
  resolveStyleQuizExposure,
  type StyleQuizView,
} from '../hooks/styleQuizExposureTracking';

interface StyleQuizDialogProps {
  open: boolean;
  deviceType: EPlatform;
  data?: StyleHotProductStyleQuizData | null;
  onClose: () => void;
  onViewStyle?: (styleName: string) => void;
  onStylePopupExposure?: (popupType: string, popupContent: string) => void;
  onStylePopupClick?: (popupType: string, popupContent: string, btnName: string) => void;
}

type AnswerSelectionMap = Record<number, string>;

interface CachedStyleQuizResult {
  answers: AnswerSelectionMap;
  completedAt: string;
  styleName: string;
}

const STYLE_QUIZ_STORAGE_KEY = 'style_test_result';
const EMPTY_QUESTIONS: StyleHotProductStyleQuizQuestion[] = [];
const EMPTY_RESULTS: StyleHotProductStyleQuizResult[] = [];
const STYLE_QUIZ_PRIMARY_COLOR = '#463ae8';
const STYLE_QUIZ_WAP_BOTTOM_GUARD = 'calc(env(safe-area-inset-bottom, 0px) + 36px)';

function readCachedResult(): CachedStyleQuizResult | null {
  try {
    const rawCache = window.localStorage.getItem(STYLE_QUIZ_STORAGE_KEY);

    if (!rawCache) {
      return null;
    }

    const parsed = JSON.parse(rawCache) as CachedStyleQuizResult;

    if (!parsed || !parsed.answers || !parsed.completedAt) {
      return null;
    }

    return parsed;
  } catch {
    return null;
  }
}

function writeCachedResult(result: CachedStyleQuizResult) {
  try {
    window.localStorage.setItem(STYLE_QUIZ_STORAGE_KEY, JSON.stringify(result));
  } catch {
    // localStorage may be unavailable in private mode; the quiz can still complete in memory.
  }
}

function formatTemplate(template: string, values: Record<string, string | number>) {
  return Object.entries(values).reduce(
    (result, [key, value]) => result.split(`{${key}}`).join(String(value)),
    template,
  );
}

function getResultByStyleName(
  results: StyleHotProductStyleQuizResult[],
  fallbackResult: StyleHotProductStyleQuizResult | undefined,
  styleName: string,
) {
  return results.find((result) => result.style_title === styleName) || fallbackResult;
}

export default function StyleQuizDialog({
  open,
  deviceType,
  data,
  onClose,
  onViewStyle,
  onStylePopupExposure,
  onStylePopupClick,
}: StyleQuizDialogProps) {
  const isWeb = deviceType === EPlatform.WEB;
  const questionList = data?.questions || EMPTY_QUESTIONS;
  const resultList = data?.result || EMPTY_RESULTS;
  const totalQuestionCount = questionList.length;
  const fallbackResult = resultList[0];
  const { buttonText, tipsText } = data || {};
  const [view, setView] = useState<StyleQuizView>('welcome');
  const [activeQuestionIndex, setActiveQuestionIndex] = useState(0);
  const [selectedAnswers, setSelectedAnswers] = useState<AnswerSelectionMap>({});
  const [cachedResult, setCachedResult] = useState<CachedStyleQuizResult | null>(null);
  const [isQuizInitialized, setIsQuizInitialized] = useState(false);
  const lastTrackedPopupPageRef = useRef<string | null>(null);
  const hasQuizData = Boolean(buttonText && tipsText && totalQuestionCount > 0 && fallbackResult);

  useLayoutEffect(() => {
    if (!open || !hasQuizData) {
      setIsQuizInitialized(false);
      return;
    }

    const storedResult = readCachedResult();

    if (storedResult) {
      const storedStyleName = calculateMatchedStyleTitle(
        questionList,
        storedResult.answers,
        resultList,
      );

      setCachedResult({ ...storedResult, styleName: storedStyleName });
      setSelectedAnswers(storedResult.answers);
      setActiveQuestionIndex(totalQuestionCount - 1);
      setView('result');
      setIsQuizInitialized(true);
      return;
    }

    setCachedResult(null);
    setSelectedAnswers({});
    setActiveQuestionIndex(0);
    setView('welcome');
    setIsQuizInitialized(true);
  }, [hasQuizData, open, questionList, resultList, totalQuestionCount]);

  const activeQuestion = questionList[activeQuestionIndex];
  const selectedAnswerId = activeQuestion ? selectedAnswers[activeQuestion.question_id] : undefined;
  const isLastQuestion = activeQuestionIndex === totalQuestionCount - 1;
  const resultStyleName = cachedResult?.styleName || fallbackResult?.style_title || '';
  const resultData = useMemo(
    () => getResultByStyleName(resultList, fallbackResult, resultStyleName),
    [fallbackResult, resultList, resultStyleName],
  );
  const resultTitlePrefix = useMemo(
    () => formatTemplate(tipsText?.style_title || '', { 's%': '' }).trim(),
    [tipsText?.style_title],
  );
  const nextButtonText = useMemo(
    () =>
      formatTemplate(buttonText?.next || '', {
        's%': activeQuestionIndex + 1,
        's1%': totalQuestionCount,
      }),
    [activeQuestionIndex, buttonText?.next, totalQuestionCount],
  );
  const dissatisfiedText = useMemo(
    () => formatTemplate(buttonText?.dissatisfied || '', { 's%': buttonText?.retest || '' }),
    [buttonText?.dissatisfied, buttonText?.retest],
  );
  const currentPopupTrackingInfo = useMemo(() => {
    if (view === 'welcome') {
      return {
        popupType: '风格测试-欢迎弹窗',
        popupContent: tipsText?.welcome_title || '',
      };
    }

    if (view === 'question') {
      return {
        popupType: '风格测试-答题弹窗',
        popupContent: activeQuestion
          ? `Q${activeQuestion.question_id}. ${activeQuestion.question_text}`
          : '',
      };
    }

    return {
      popupType: '风格测试-结果弹窗',
      popupContent: resultData?.style_title || resultStyleName,
    };
  }, [activeQuestion, resultData?.style_title, resultStyleName, tipsText?.welcome_title, view]);
  const currentPopupContent = useMemo(
    () =>
      getStyleQuizPopupContent(
        view,
        activeQuestion?.question_id,
        currentPopupTrackingInfo.popupContent,
      ),
    [activeQuestion?.question_id, currentPopupTrackingInfo.popupContent, view],
  );
  const currentPopupExposurePageKey = getStyleQuizExposurePageKey(
    view,
    activeQuestionIndex,
    activeQuestion?.question_id,
  );

  useEffect(() => {
    if (!open) {
      lastTrackedPopupPageRef.current = null;
      return;
    }

    if (!isQuizInitialized || !hasQuizData) {
      return;
    }

    const exposureDecision = resolveStyleQuizExposure(
      lastTrackedPopupPageRef.current,
      currentPopupExposurePageKey,
    );

    lastTrackedPopupPageRef.current = exposureDecision.nextPageKey;

    if (!exposureDecision.shouldTrack) {
      return;
    }

    onStylePopupExposure?.(currentPopupTrackingInfo.popupType, currentPopupContent);
  }, [
    currentPopupContent,
    currentPopupExposurePageKey,
    currentPopupTrackingInfo.popupType,
    hasQuizData,
    isQuizInitialized,
    onStylePopupExposure,
    open,
  ]);

  const trackCurrentPopupButtonClick = useCallback(
    (btnName: string) => {
      onStylePopupClick?.(currentPopupTrackingInfo.popupType, currentPopupContent, btnName);
    },
    [currentPopupContent, currentPopupTrackingInfo.popupType, onStylePopupClick],
  );

  const handleCloseButtonClick = useCallback(() => {
    trackCurrentPopupButtonClick('Close');
    onClose();
  }, [onClose, trackCurrentPopupButtonClick]);

  const handleBegin = useCallback(() => {
    trackCurrentPopupButtonClick(buttonText?.begin || '');
    setCachedResult(null);
    setSelectedAnswers({});
    setActiveQuestionIndex(0);
    setView('question');
  }, [buttonText?.begin, trackCurrentPopupButtonClick]);

  const handleSelectAnswer = useCallback(
    (answerId: string) => {
      if (!activeQuestion) {
        return;
      }

      setSelectedAnswers((prev) => ({
        ...prev,
        [activeQuestion.question_id]: answerId,
      }));
    },
    [activeQuestion],
  );

  const handlePrevious = useCallback(() => {
    trackCurrentPopupButtonClick(buttonText?.previousStep || '');
    setActiveQuestionIndex((prev) => Math.max(prev - 1, 0));
  }, [buttonText?.previousStep, trackCurrentPopupButtonClick]);

  const handleNext = useCallback(() => {
    if (!activeQuestion || !selectedAnswerId) {
      return;
    }

    trackCurrentPopupButtonClick(isLastQuestion ? buttonText?.submit || '' : nextButtonText);

    if (!isLastQuestion) {
      setActiveQuestionIndex((prev) => Math.min(prev + 1, totalQuestionCount - 1));
      return;
    }

    const styleName = calculateMatchedStyleTitle(questionList, selectedAnswers, resultList);
    const completedResult: CachedStyleQuizResult = {
      answers: selectedAnswers,
      completedAt: new Date().toISOString(),
      styleName,
    };

    setCachedResult(completedResult);
    writeCachedResult(completedResult);
    setView('result');
  }, [
    activeQuestion,
    isLastQuestion,
    questionList,
    resultList,
    selectedAnswerId,
    selectedAnswers,
    buttonText?.submit,
    nextButtonText,
    trackCurrentPopupButtonClick,
    totalQuestionCount,
  ]);

  const handleRetest = useCallback(() => {
    trackCurrentPopupButtonClick(buttonText?.retest || '');
    setCachedResult(null);
    setSelectedAnswers({});
    setActiveQuestionIndex(0);
    setView('question');
  }, [buttonText?.retest, trackCurrentPopupButtonClick]);

  const handleViewStyle = useCallback(() => {
    trackCurrentPopupButtonClick(buttonText?.jump || '');
    onViewStyle?.(resultData?.style_title || resultStyleName);
    onClose();
  }, [
    buttonText?.jump,
    onClose,
    onViewStyle,
    resultData?.style_title,
    resultStyleName,
    trackCurrentPopupButtonClick,
  ]);

  if (!hasQuizData || !buttonText || !tipsText) {
    return null;
  }

  if (!isWeb) {
    return (
      <Popup
        open={open}
        position="bottom"
        height="560px"
        round
        borderRadius={20}
        overlay
        closeOnClickOverlay
        onClose={onClose}
        hideCloseIcon
        bodyClassName="style-quiz-wap-popup-content"
        sx={{
          '& .MuiDrawer-paper': {
            height: '560px',
            maxHeight: '560px',
            borderTopLeftRadius: '20px',
            borderTopRightRadius: '20px',
            overflow: 'hidden',
            boxShadow: 'none',
          },
          '& .MuiDrawer-paper > .MuiBox-root': {
            overflow: 'hidden',
            borderTopLeftRadius: '20px',
            borderTopRightRadius: '20px',
          },
          '& .popup-content.style-quiz-wap-popup-content': {
            display: 'flex',
            height: '100%',
            minHeight: 0,
            overflow: 'hidden',
            padding: '0 !important',
          },
        }}
      >
        <div className="relative flex h-full w-full flex-col overflow-hidden bg-white text-primary-black">
          <button
            type="button"
            aria-label="Close"
            onClick={handleCloseButtonClick}
            className="absolute right-[12px] top-[12px] z-20 flex h-[22px] w-[22px] items-center justify-center border-0 bg-transparent p-0 text-primary-black transition-opacity hover:opacity-70"
          >
            <CloseOutlined className="text-[22px]" />
          </button>

          {view === 'welcome' && (
            <WelcomeViewWap
              title={tipsText.welcome_title}
              description={tipsText.welcome_describe}
              imageSrc={tipsText.welcome_page_bg}
              buttonText={buttonText.begin}
              onBegin={handleBegin}
            />
          )}

          {view === 'question' && activeQuestion && (
            <QuestionViewWap
              question={activeQuestion}
              selectedAnswerId={selectedAnswerId}
              isFirstQuestion={activeQuestionIndex === 0}
              nextButtonText={isLastQuestion ? buttonText.submit : nextButtonText}
              previousButtonText={buttonText.previousStep}
              onSelectAnswer={handleSelectAnswer}
              onPrevious={handlePrevious}
              onNext={handleNext}
            />
          )}

          {view === 'result' && (
            <ResultViewWap
              titlePrefix={resultTitlePrefix}
              styleName={resultData?.style_title || resultStyleName}
              description={resultData?.style_describe || ''}
              imageSrc={resultData?.style_img}
              jumpText={buttonText.jump}
              dissatisfiedText={dissatisfiedText}
              retestText={buttonText.retest}
              onViewStyle={handleViewStyle}
              onRetest={handleRetest}
            />
          )}
        </div>
      </Popup>
    );
  }

  return (
    <Dialog.Info
      open={open}
      onClose={onClose}
      hideHeader
      ariaLabel={tipsText.welcome_title}
      closeOnClickOverlay
      maxWidth={false}
      sx={{
        '& .MuiDialog-container': {
          padding: isWeb ? '24px' : '18px',
        },
        '& .MuiDialog-paper': {
          width: isWeb ? '584px' : 'min(342px, calc(100vw - 36px))',
          maxWidth: 'calc(100vw - 36px)',
          margin: 0,
          borderRadius: '12px',
          overflow: 'hidden',
          backgroundColor: '#fff',
          boxShadow: 'none',
        },
      }}
      contentProps={{
        sx: {
          padding: '0 !important',
          overflow: 'visible',
        },
      }}
    >
      <div className="relative overflow-hidden rounded-[12px] bg-white text-primary-black">
        <button
          type="button"
          aria-label="Close"
          onClick={handleCloseButtonClick}
          className="absolute right-[24px] top-[24px] z-20 flex h-[22px] w-[22px] items-center justify-center border-0 bg-transparent p-0 text-primary-black transition-opacity hover:opacity-70 max-md:right-[16px] max-md:top-[16px]"
        >
          <CloseOutlined className="text-[22px]" />
        </button>

        {view === 'welcome' && (
          <WelcomeView
            title={tipsText.welcome_title}
            description={tipsText.welcome_describe}
            imageSrc={tipsText.welcome_page_bg}
            buttonText={buttonText.begin}
            onBegin={handleBegin}
          />
        )}

        {view === 'question' && activeQuestion && (
          <QuestionView
            question={activeQuestion}
            selectedAnswerId={selectedAnswerId}
            isFirstQuestion={activeQuestionIndex === 0}
            nextButtonText={isLastQuestion ? buttonText.submit : nextButtonText}
            previousButtonText={buttonText.previousStep}
            onSelectAnswer={handleSelectAnswer}
            onPrevious={handlePrevious}
            onNext={handleNext}
          />
        )}

        {view === 'result' && (
          <ResultView
            titlePrefix={resultTitlePrefix}
            styleName={resultData?.style_title || resultStyleName}
            description={resultData?.style_describe || ''}
            imageSrc={resultData?.style_img}
            jumpText={buttonText.jump}
            dissatisfiedText={dissatisfiedText}
            retestText={buttonText.retest}
            onViewStyle={handleViewStyle}
            onRetest={handleRetest}
          />
        )}
      </div>
    </Dialog.Info>
  );
}

function StyleQuizPrimaryButton({
  children,
  onClick,
  disabled,
  className = '',
}: {
  children: string;
  onClick: () => void;
  disabled?: boolean;
  className?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`flex h-[44px] items-center justify-center rounded-[22px] border-0 bg-[#463ae8] px-[24px] font-bs text-[14px] leading-[17px] text-white transition-opacity enabled:hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40 ${className}`}
      style={{ backgroundColor: STYLE_QUIZ_PRIMARY_COLOR }}
    >
      {children}
    </button>
  );
}

function WelcomeViewWap({
  title,
  description,
  imageSrc,
  buttonText,
  onBegin,
}: {
  title: string;
  description: string;
  imageSrc?: string;
  buttonText: string;
  onBegin: () => void;
}) {
  return (
    <div className="flex h-full w-full flex-col overflow-hidden bg-white">
      <div className="relative h-[210px] w-full shrink-0 overflow-hidden bg-[#f0f0f0]">
        {imageSrc ? (
          <Image
            src={imageSrc}
            alt={title}
            fill
            sizes="100vw"
            className="block h-full w-full object-cover object-center"
          />
        ) : null}
      </div>

      <div className="flex min-h-0 flex-1 flex-col text-center">
        <div className="min-h-0 flex-1 overflow-y-auto px-[12px] pt-[36px]">
          <div className="flex w-full flex-col items-center gap-[16px]">
            <h2 className="w-full font-bs text-[20px] leading-[24px] text-primary-black">
              {title}
            </h2>
            <p className="max-h-[170px] w-full overflow-y-auto whitespace-pre-wrap font-br text-[14px] leading-[17px] text-[#282828]">
              {description}
            </p>
          </div>
        </div>

        <div
          className="flex shrink-0 justify-center px-[12px] pt-[12px]"
          style={{ paddingBottom: STYLE_QUIZ_WAP_BOTTOM_GUARD }}
        >
          <StyleQuizPrimaryButton className="w-[280px] max-w-full" onClick={onBegin}>
            {buttonText}
          </StyleQuizPrimaryButton>
        </div>
      </div>
    </div>
  );
}

function QuestionViewWap({
  question,
  selectedAnswerId,
  isFirstQuestion,
  nextButtonText,
  previousButtonText,
  onSelectAnswer,
  onPrevious,
  onNext,
}: {
  question: StyleHotProductStyleQuizQuestion;
  selectedAnswerId?: string;
  isFirstQuestion: boolean;
  nextButtonText: string;
  previousButtonText: string;
  onSelectAnswer: (answerId: string) => void;
  onPrevious: () => void;
  onNext: () => void;
}) {
  const renderAnswerButton = (answer: StyleHotProductStyleQuizQuestion['answers'][number]) => {
    const isSelected = selectedAnswerId === answer.answer_id;

    return (
      <button
        key={answer.answer_id}
        type="button"
        aria-pressed={isSelected}
        onClick={() => onSelectAnswer(answer.answer_id)}
        className={`flex min-h-[53px] w-full shrink-0 items-center rounded-[6px] border bg-[#f4f4f4] px-[16px] py-[15px] text-left transition-colors ${
          isSelected ? 'border-[#463ae8]' : 'border-transparent hover:border-[#463ae8]'
        } focus:outline-none focus-visible:ring-2 focus-visible:ring-[#463ae8]`}
      >
        <span className="whitespace-pre-wrap break-words font-br text-[14px] leading-[21px] text-primary-black">
          {answer.answer_sort}. {answer.answer_text}
        </span>
      </button>
    );
  };

  return (
    <div className="flex h-full w-full flex-col overflow-hidden bg-white">
      <div className="shrink-0 px-[12px] pt-[32px]">
        <h2 className="w-full whitespace-pre-wrap break-words pr-[28px] font-bs text-[16px] leading-[24px] text-primary-black">
          {`Q${question.question_id}. ${question.question_text}`}
        </h2>
      </div>

      <div className="mt-[16px] min-h-0 flex-1 overflow-y-auto px-[12px] pb-[16px]">
        <div className="flex flex-col gap-[8px]">{question.answers.map(renderAnswerButton)}</div>
      </div>

      <div
        className={`grid h-[62px] shrink-0 items-center gap-[16px] border-t border-[#f4f4f4] bg-white px-[12px] py-[12px] ${
          isFirstQuestion ? 'grid-cols-1' : 'grid-cols-[128px_minmax(0,1fr)]'
        }`}
      >
        {!isFirstQuestion && (
          <button
            type="button"
            onClick={onPrevious}
            className="flex h-[38px] w-full items-center justify-center rounded-[19px] border border-primary-black bg-white px-[16px] font-bs text-[14px] leading-[17px] text-primary-black transition-opacity hover:opacity-90"
          >
            {previousButtonText}
          </button>
        )}
        <StyleQuizPrimaryButton
          className="h-[38px] w-full"
          onClick={onNext}
          disabled={!selectedAnswerId}
        >
          {nextButtonText}
        </StyleQuizPrimaryButton>
      </div>
    </div>
  );
}

function ResultViewWap({
  titlePrefix,
  styleName,
  description,
  imageSrc,
  jumpText,
  dissatisfiedText,
  retestText,
  onViewStyle,
  onRetest,
}: {
  titlePrefix: string;
  styleName: string;
  description: string;
  imageSrc?: string;
  jumpText: string;
  dissatisfiedText: string;
  retestText: string;
  onViewStyle: () => void;
  onRetest: () => void;
}) {
  const retestPromptText = dissatisfiedText.endsWith(retestText)
    ? dissatisfiedText.slice(0, -retestText.length)
    : dissatisfiedText;

  return (
    <div className="flex h-full w-full flex-col overflow-y-auto bg-white px-[12px] pb-[36px] pt-[48px]">
      <div className="flex h-[310px] w-full shrink-0 flex-col items-center rounded-[6px] bg-[#f4f4f4] px-[23px] py-[32px]">
        <div className="flex w-full flex-col items-center text-center text-primary-black">
          <p className="font-bs text-[14px] leading-[17px]">{titlePrefix}</p>
          <p className="mt-[4px] font-bs text-[40px] leading-[48px] text-[#463ae8]">{styleName}</p>
          <p className="mt-[4px] w-full whitespace-pre-wrap font-br text-[12px] leading-[14px]">
            {description}
          </p>
        </div>

        <div className="relative mt-[24px] h-[133px] w-full overflow-hidden bg-[#d0d0d0]">
          {imageSrc ? (
            <Image
              src={imageSrc}
              alt={styleName}
              fill
              sizes="305px"
              className="block h-full w-full object-cover object-center"
            />
          ) : null}
        </div>
      </div>

      <div className="mt-[32px] flex w-full flex-col items-center">
        <StyleQuizPrimaryButton className="w-[281px]" onClick={onViewStyle}>
          {jumpText}
        </StyleQuizPrimaryButton>

        <p className="mt-[16px] w-[291px] text-center font-br text-[14px] leading-[17px] text-primary-black">
          {retestPromptText}
          <button
            type="button"
            onClick={onRetest}
            className="border-0 bg-transparent p-0 font-br text-[14px] leading-[17px] text-primary-black underline decoration-solid underline-offset-2"
          >
            {retestText}
          </button>
        </p>
      </div>
    </div>
  );
}

function WelcomeView({
  title,
  description,
  imageSrc,
  buttonText,
  onBegin,
}: {
  title: string;
  description: string;
  imageSrc?: string;
  buttonText: string;
  onBegin: () => void;
}) {
  return (
    <div className="relative flex h-[582px] max-h-[calc(100vh-36px)] flex-col overflow-hidden bg-white">
      <div
        className="h-[232px] shrink-0 bg-[#d0d0d0] bg-cover bg-center"
        style={imageSrc ? { backgroundImage: `url(${imageSrc})` } : undefined}
      />

      <div className="flex min-h-0 flex-1 flex-col items-center px-[32px] pb-[32px] pt-[37px] text-center max-md:px-[20px] max-md:pt-[28px]">
        <div className="flex min-h-0 w-full max-w-[520px] flex-1 flex-col items-center gap-[16px] overflow-hidden">
          <h2 className="font-bb text-[24px] capitalize leading-[29px] text-primary-black">
            {title}
          </h2>
          <p className="min-h-0 overflow-y-auto whitespace-pre-wrap font-br text-[16px] leading-[21px] text-[#282828]">
            {description}
          </p>
        </div>

        <button
          type="button"
          onClick={onBegin}
          className="mt-[32px] flex h-[48px] w-full max-w-[420px] shrink-0 items-center justify-center rounded-[6px] border-0 bg-[#463ae8] px-[24px] font-bs text-[16px] leading-[20px] text-white transition-opacity hover:opacity-90"
        >
          {buttonText}
        </button>
      </div>
    </div>
  );
}

function QuestionView({
  question,
  selectedAnswerId,
  isFirstQuestion,
  nextButtonText,
  previousButtonText,
  onSelectAnswer,
  onPrevious,
  onNext,
}: {
  question: StyleHotProductStyleQuizQuestion;
  selectedAnswerId?: string;
  isFirstQuestion: boolean;
  nextButtonText: string;
  previousButtonText: string;
  onSelectAnswer: (answerId: string) => void;
  onPrevious: () => void;
  onNext: () => void;
}) {
  const renderAnswerButton = (answer: StyleHotProductStyleQuizQuestion['answers'][number]) => {
    const isSelected = selectedAnswerId === answer.answer_id;

    return (
      <button
        key={answer.answer_id}
        type="button"
        aria-pressed={isSelected}
        onClick={() => onSelectAnswer(answer.answer_id)}
        className={`flex min-h-[49px] w-full shrink-0 items-center overflow-hidden rounded-[8px] border bg-[#f4f4f4] p-[16px] text-left transition-colors ${
          isSelected ? 'border-[#463ae8]' : 'border-transparent hover:border-[#463ae8]'
        } focus:outline-none focus-visible:ring-2 focus-visible:ring-[#463ae8]`}
      >
        <span className="whitespace-pre-wrap break-words font-br text-[14px] leading-[normal] text-primary-black">
          {answer.answer_sort}. {answer.answer_text}
        </span>
      </button>
    );
  };

  return (
    <div className="flex h-[582px] max-h-[calc(100vh-36px)] flex-col overflow-hidden bg-white">
      <div className="flex h-[494px] shrink-0 flex-col overflow-hidden pt-[48px] pb-[16px] pl-[32px] max-md:pl-[20px]">
        <h2 className="shrink-0 whitespace-pre-wrap break-words pr-[32px] font-bb text-[16px] leading-[24px] text-primary-black max-md:pr-[20px]">
          {`Q${question.question_id}. ${question.question_text}`}
        </h2>

        <div
          key={question.question_id}
          className="style-quiz-answer-scroll mt-[24px] min-h-0 flex-1 overflow-y-auto pr-[21px]"
        >
          <div className="flex w-[520px] max-w-full flex-col gap-[16px] max-md:w-[calc(100%-20px)]">
            {question.answers.map(renderAnswerButton)}
          </div>
        </div>
      </div>

      <div
        className={`flex h-[88px] shrink-0 items-end justify-center gap-[16px] px-[82px] pb-[24px] pt-[16px] max-md:px-[20px] ${
          isFirstQuestion ? '' : 'max-md:grid max-md:grid-cols-2'
        }`}
      >
        {!isFirstQuestion && (
          <button
            type="button"
            onClick={onPrevious}
            className="flex h-[48px] w-[160px] items-center justify-center rounded-[6px] border border-primary-black bg-white px-[16px] font-bs text-[16px] leading-[20px] text-primary-black transition-opacity hover:opacity-90 max-md:w-full"
          >
            {previousButtonText}
          </button>
        )}
        <button
          type="button"
          onClick={onNext}
          disabled={!selectedAnswerId}
          className="flex h-[48px] min-w-0 flex-1 items-center justify-center whitespace-pre-wrap rounded-[6px] border-0 bg-[#463ae8] px-[24px] font-bs text-[16px] leading-[20px] text-white transition-opacity enabled:hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40"
        >
          {nextButtonText}
        </button>
      </div>
      <style jsx global>{`
        .style-quiz-answer-scroll {
          scrollbar-color: #ddd transparent;
          scrollbar-width: thin;
          overscroll-behavior: contain;
        }

        .style-quiz-answer-scroll::-webkit-scrollbar {
          width: 4px;
        }

        .style-quiz-answer-scroll::-webkit-scrollbar-track {
          background: transparent;
        }

        .style-quiz-answer-scroll::-webkit-scrollbar-thumb {
          background: #ddd;
          border-radius: 90px;
        }
      `}</style>
    </div>
  );
}

function ResultView({
  titlePrefix,
  styleName,
  description,
  imageSrc,
  jumpText,
  dissatisfiedText,
  retestText,
  onViewStyle,
  onRetest,
}: {
  titlePrefix: string;
  styleName: string;
  description: string;
  imageSrc?: string;
  jumpText: string;
  dissatisfiedText: string;
  retestText: string;
  onViewStyle: () => void;
  onRetest: () => void;
}) {
  const retestPromptText = dissatisfiedText.endsWith(retestText)
    ? dissatisfiedText.slice(0, -retestText.length)
    : dissatisfiedText;

  return (
    <div className="style-quiz-result-scroll flex h-[582px] max-h-[calc(100vh-36px)] flex-col overflow-y-auto bg-white px-[32px] pb-[30px] pt-[55px] max-md:px-[20px]">
      <div className="flex w-full flex-col items-center gap-[32px]">
        <div className="flex h-[382px] w-full flex-col items-center rounded-[12px] bg-[#f4f4f4] px-[60px] py-[44px] max-md:h-auto max-md:px-[20px]">
          <div className="flex flex-col items-center gap-[16px]">
            <div className="flex w-full flex-col items-center gap-[8px] text-center font-bb text-primary-black">
              <div className="flex w-full flex-col items-center gap-[4px]">
                <p className="text-[24px] leading-[normal]">{titlePrefix}</p>
                <p className="text-[48px] leading-[normal] text-[#463ae8]">{styleName}</p>
              </div>
              <p className="w-full whitespace-pre-wrap text-[16px] leading-[normal]">
                {description}
              </p>
            </div>

            <div className="h-[160px] w-[420px] overflow-hidden rounded-[4px] bg-[#b2b2b2] max-md:w-full">
              {imageSrc ? (
                <Image
                  src={imageSrc}
                  alt={styleName}
                  className="h-full w-full object-cover"
                  style={{ objectPosition: 'center 35%' }}
                  width={420}
                  height={160}
                />
              ) : null}
            </div>
          </div>
        </div>

        <div className="flex w-[420px] flex-col items-center gap-[16px] max-md:w-full">
          <button
            type="button"
            onClick={onViewStyle}
            className="flex h-[48px] w-full items-center justify-center rounded-[6px] border-0 bg-[#463ae8] px-[24px] font-bs text-[16px] leading-[20px] text-white transition-opacity hover:opacity-90"
          >
            {jumpText}
          </button>
          <p className="font-br text-[16px] leading-[normal] text-primary-black">
            {retestPromptText}
            <button
              type="button"
              onClick={onRetest}
              className="border-0 bg-transparent p-0 font-br text-[16px] leading-[normal] text-primary-black underline decoration-solid underline-offset-2"
            >
              {retestText}
            </button>
          </p>
        </div>
      </div>
      <style jsx global>{`
        .style-quiz-result-scroll {
          -ms-overflow-style: none;
          scrollbar-width: none;
          overscroll-behavior: contain;
        }

        .style-quiz-result-scroll::-webkit-scrollbar {
          display: none;
          width: 0;
          height: 0;
        }
      `}</style>
    </div>
  );
}
